import type { CSSProperties } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { getContrastHex } from '@/lib/colorUtils';

export interface Layout6ThemeColors {
  header: string;
  menu: string;
  primary: string;
  secondary: string;
  button: string;
  title: string;
  text: string;
}

/**
 * Cores do config — escopo limitado (não afeta fundos de página/cards):
 * - header  → fundo da barra principal do header
 * - menu    → topbar, links/ícones do menu, botão de serviço
 * - primary → fundo do footer e blocos CTA explícitos (.wl6-cta)
 * - secondary → badges secundários
 * - button  → CTAs, preços, tabs ativas, accent
 * - title   → títulos de seção (sobre fundo claro fixo)
 * - text    → textos de corpo (sobre fundo claro fixo)
 *
 * Fundos estruturais (--wl6-bg, --wl6-surface, etc.) ficam fixos em styles.css.
 */
export function getLayout6ThemeStyle(colors: Layout6ThemeColors): CSSProperties {
  return {
    '--wl6-accent': colors.button,
    '--l6-accent': colors.button,
    '--wl6-primary': colors.primary,
    '--wl6-secondary': colors.secondary,
    '--wl6-button': colors.button,
    '--wl6-title': colors.title,
    '--wl6-text': colors.text,
    '--wl6-header': colors.header,
    '--wl6-menu': colors.menu,
    '--wl6-on-primary': getContrastHex(colors.primary),
    '--wl6-on-button': getContrastHex(colors.button),
    '--wl6-accent-bg': `${colors.button}12`,
    '--wl6-primary-bg': `${colors.primary}12`,
  } as CSSProperties;
}

export const WL6_THEME_KEYS = [
  '--wl6-accent',
  '--l6-accent',
  '--wl6-primary',
  '--wl6-secondary',
  '--wl6-button',
  '--wl6-title',
  '--wl6-text',
  '--wl6-header',
  '--wl6-menu',
  '--wl6-on-primary',
  '--wl6-on-button',
  '--wl6-accent-bg',
  '--wl6-primary-bg',
] as const;

export function useLayout6Theme() {
  const { colors } = useTheme();

  return {
    colors,
    themeStyle: getLayout6ThemeStyle(colors),
    headerColor: colors.header,
    menuColor: colors.menu,
    primaryColor: colors.primary,
    secondaryColor: colors.secondary,
    buttonColor: colors.button,
    titleColor: colors.title,
    textColor: colors.text,
  };
}
